import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Switch, Route } from "react-router-dom"
import { useQuery } from '@apollo/client'
import { collection, getDocs, query, where, updateDoc, doc, onSnapshot, orderBy } from "firebase/firestore"
import { db, addPrefix } from './firebase'
import Admin from './Admin/Admin'
import Agent from './Agent/Agent'
import Checkin from './Checkin/Checkin'
import Login from './Login/Login'
import gql from "graphql-tag"
import ErrorModal from './Components/ErrorModal'
import QueueContainer from './Agent/QueueContainer'
import { app } from './firebase'
import { getAuth, signInAnonymously } from 'firebase/auth'
import Home from './Home/Home'
import { DeviceProvider } from './Context/DeviceContext'
import loader from "./assets/fast-loader-blue.gif"

const USER_QUERY = gql`query CurrentUser{
  currentUser{
    id
    username
    email
    groupNames
  }  
}`


function App() {
  const [currentUser, setCurrentUser] = useState({ id: null, pk: null, token: localStorage.getItem('buggy_vms_user'), groupNames: null, username: null, status: null, email: null })
  const [allAgents, setAllAgents] = useState([])
  const [activeDrivers, setActiveDrivers] = useState([])
  const [allLocations, setAllLocations] = useState([])
  const [allLanguages, setAllLanguages] = useState([])
  const [allDepartments, setAllDepartments] = useState([])
  const [allBranches, setAllBranches] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const auth = getAuth(app)

  useQuery(USER_QUERY, {
    skip: !currentUser.id || !currentUser.pk || !currentUser.token,
    onError: (err) => { setLoading(false); setError(err) },
    onCompleted: async (response) => {
      if (response && response.currentUser && response.currentUser.groupNames) {
        setCurrentUser({ ...currentUser, groupNames: response.currentUser.groupNames, username: response.currentUser.username, email: response.currentUser.email })
        setLoading(false)
      }
    }
  })

  const handleLogout = async () => {
    try {
      if (currentUser.pk) {
        if (activeDrivers.filter(driver => driver.agent === currentUser.pk && driver.status === "assigned").length > 0) {
          setError('Error: Please complete your assigned task in order to logout!')
          return
        }

        await updateDoc(doc(db, addPrefix("users"), String(currentUser.pk)), {
          status: "inactive",
          location: null
        })
      }
      handleLogout()
    } catch (err) {
      setError("Error: Unable to logout user!")
    }

    await localStorage.removeItem('buggy_vms_user')
    window.location.reload(false)
  }

  const getUser = async () => {
    setLoading(true)
    // debugger
    if (currentUser.token) {
      const querySnapshot = await getDocs(query(collection(db, addPrefix("users")), where("token", "==", currentUser.token)))
      let tmp = true
      await querySnapshot?.forEach((doc) => {
        tmp = false
        let data = doc.data()
        setCurrentUser({
          ...currentUser,
          id: data.userId,
          pk: data.userPk,
          status: data.status,
          location: data.location,
          isAgent: data.isAgent,
          language: data.language,
          selectedBranch: data.selectedBranch,
        })
      })
      if (tmp) handleLogout()
    } else {
      setCurrentUser({ id: null, pk: null, token: null, groupNames: null, username: null, status: null })
      setLoading(false)
    }
  }

  const signinAnonymously = () => {
    if (auth.currentUser == null || localStorage.getItem('buggy_vms_firebase_user')) {
      signInAnonymously(auth).then((userCredential) => {
        const user = userCredential.user
        localStorage.setItem('buggy_vms_firebase_user', user.uid)
      }).catch((error) => {
        console.error("Error signing in anonymously:", error)
      })
    } else {
      console.log("user already logged in")
    }
  }

  //To get all languages
  const loadLanguages = async () => {
    const querySnapshot = await getDocs(query(collection(db, addPrefix("languages")), orderBy("status", "asc")))
    setAllLanguages(querySnapshot.docs.map((doc) => ({ ...doc.data(), firebaseId: doc.id })))
  }
  //To get all locations
  const loadLocations = async () => {
    const querySnapshot = await getDocs(query(collection(db, addPrefix("locations")), orderBy("status", "asc")))
    setAllLocations(querySnapshot.docs.map((doc) => ({ ...doc.data(), firebaseId: doc.id })))
  }
  //To get all Department
  const loadDepartments = async () => {
    const querySnapshot = await getDocs(query(collection(db, addPrefix("departments")), orderBy("status", "asc")))
    setAllDepartments(querySnapshot.docs.map((doc) => ({ ...doc.data(), firebaseId: doc.id })))
  }

  const loadBranches = async () => {
    const querySnapshot = await getDocs(query(collection(db, addPrefix("branches")), orderBy("status", "asc")))
    setAllBranches(querySnapshot.docs.map((doc) => ({ ...doc.data(), firebaseId: doc.id })))
  }

  useEffect(() => {
    signinAnonymously()
    loadLanguages()
    loadLocations()
    loadDepartments()
    loadBranches()
  }, [])

  useEffect(() => {
    getUser()
  }, [currentUser.token])

  //To get all users/agents
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, addPrefix("users")), orderBy("status", "asc")),
      (querySnapshot) => {
        setAllAgents(querySnapshot.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id })))
      }
    )
    return () => unsubscribe()
  }, [])

  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (!hash || !allBranches?.length) return

    const currentBranch = allBranches.find(b => b.customId === hash)
    if (!currentBranch) return

    const branchRules = (currentBranch.rules || []).map(rule => {
      return { field: rule.field, op: rule.operation, val: rule.value }
    })

    let q = query(collection(db, addPrefix("tickets")))
    q = query(q, where("status", "in", ["waiting", "assigned"]))
    if (branchRules.length > 0) {
      branchRules.forEach(f => {
        q = query(q, where(f.field, f.op, f.val))
      })
    } else {
      q = query(q, where("customBranchId", "==", hash))
    }

    q = query(q, orderBy("dateAdded", "asc"))

    const unsubscribe = onSnapshot(q, snap => {
      // let data = null, newTickets = []
      // snap.docChanges().forEach((change) => {
      //   data = change.doc.data()
      //   // if (change.type === "added" && data.status === "assigned")
      //   //   newTickets.push({ ...data, firebaseId: change.doc.id })
      // })
      setActiveDrivers(snap.docs.map(doc => ({ ...doc.data(), firebaseId: doc.id })))
    })

    return () => unsubscribe()
  }, [allBranches])
  return (
    <DeviceProvider>
      <div className="app">
        {error && error !== "" && <ErrorModal open={!!error} handleClose={() => setError("")} message={error} />}
        <Router>
          <Switch>
            <Route path="/admin/login" component={Login} />
            <Route
              path="/admin"
              render={() => (
                <AdminRoot
                  currentUser={currentUser}
                  setCurrentUser={setCurrentUser}
                  allAgents={allAgents}
                  activeDrivers={activeDrivers}
                  allLocations={allLocations}
                  allLanguages={allLanguages}
                  allDepartments={allDepartments}
                  loadLocations={loadLocations}
                  loadLanguages={loadLanguages}
                  loadDepartments={loadDepartments}
                  handleLogout={handleLogout}
                  allBranches={allBranches}
                  loadBranches={loadBranches}
                />
              )}
            />
            <Route exact path="/checkin">
              <Checkin activeDrivers={activeDrivers} allBranches={allBranches} />
            </Route>
            <Route exact path="/queue">
              <QueueContainer allAgents={allAgents} activeDrivers={activeDrivers} locations={allLocations} allBranches={allBranches} />
            </Route>
            <Route exact path="*">
              <Home />
            </Route>
          </Switch>
        </Router>
      </div>
    </DeviceProvider>
  )
}

function AdminRoot({ currentUser, setCurrentUser, allAgents, activeDrivers, allLocations, allLanguages, allDepartments, loadLocations, loadLanguages, loadDepartments, handleLogout, allBranches, loadBranches, }) {
  const history = require('react-router-dom').useHistory()

  if (currentUser && currentUser.id && currentUser.pk && currentUser.token && currentUser.groupNames && currentUser.isAgent) {
    return (
      <Agent
        currentUser={currentUser}
        allAgents={allAgents}
        locations={allLocations}
        languages={allLanguages}
        activeDrivers={activeDrivers}
        setCurrentUser={setCurrentUser}
        handleLogout={handleLogout}
        allBranches={allBranches}
      />
    )
  } else if (currentUser && currentUser.id && currentUser.pk && currentUser.token && currentUser.groupNames && currentUser.groupNames.includes("VMS-Admin") && !currentUser.isAgent) {
    return (
      <Admin
        currentUser={currentUser}
        allAgents={allAgents}
        activeDrivers={activeDrivers}
        allLocations={allLocations}
        allLanguages={allLanguages}
        allDepartments={allDepartments}
        loadLocations={loadLocations}
        loadLanguages={loadLanguages}
        loadDepartments={loadDepartments}
        setCurrentUser={setCurrentUser}
        handleLogout={handleLogout}
        allBranches={allBranches}
        loadBranches={loadBranches}
      />
    )
  } else if (localStorage.getItem('buggy_vms_user')) {
    return <center><img src={loader} alt="" width="100" style={{ zIndex: 10000, marginBottom: -70, marginTop: 10 }} /></center>
  } else {
    history.push("/admin/login")
  }
  return null
}

export default App
